/* @flow */
import {graphql} from 'react-apollo';
import {connect} from 'react-redux';
import {compose} from 'redux';
import {pick} from 'lodash';

import filteredCountQuery from '../../queries/filteredCountQuery';
import {searchQueryParams} from '../../queries/searchQuery';
import {ResultsParams} from '../../../server/graphql/params';

import type {SearchParamsType} from '../../reducers/searchParams';
import type {State as ReduxState} from '../../reducers/index';
import type {FilteredCountQueryResponseData} from '../../queries/filteredCountQuery';
import type {FilterGroupType} from '../MoreFilters/MoreFilters';
import FilterForm from '../FilterForm/FilterForm';
import MoreFiltersPanel from '../MoreFiltersPanel/MoreFiltersPanel';

export type CallbackProps = {
    filterGroups: FilterGroupType[],
    resultsCount?: ?number,
    searchResultsLoading: boolean
};

type StateProps = {
    inputParams: SearchParamsType,
    selectedRefinements: string[],
    searchQueryLoading: boolean,
    inputParamsAreChanged: boolean
};

type WithFilteredCountDataProps = {
    data: FilteredCountQueryResponseData
};

type CallbackElementType = typeof MoreFiltersPanel | typeof FilterForm;

type Props = {
    children: (
        callbackProps: CallbackProps
    ) => React.Element<CallbackElementType>,
    filterGroups: FilterGroupType[],
    searchResultsLoading: boolean
} & StateProps &
    WithFilteredCountDataProps;

export const FilteredCountDataProvider = ({
    children,
    data,
    searchResultsLoading
}: Props) => {
    const filterGroups =
        (data && data.results && data.results.filterGroups) || [];

    return children({
        resultsCount:
            data &&
            data.results &&
            data.results.hitCollection &&
            data.results.hitCollection.total,
        filterGroups,
        searchResultsLoading
    });
};

FilteredCountDataProvider.displayName = 'FilteredCountDataProvider';

const mapStateToProps = ({
    expandedSearchGroups: {
        hasModifiedMapViewport: expandedSearchHasModifiedMapViewport
    },
    map: {bounds},
    searchParams: {
        params,
        inputParams,
        inputParamsAreChanged,
        inputParamsAreUpdating,
        selectedFilters
    },
    system: {siteName}
}: ReduxState) => ({
    bounds,
    expandedSearchHasModifiedMapViewport,
    params,
    inputParams,
    inputParamsAreChanged,
    inputParamsAreUpdating,
    siteName,
    selectedFilters
});

// We are going to cache parameters to the apollo HOC as a means to skip apollo request while maintaining state
// NOTE: `skip` resulted in the number of results being hidden temporarily
let requestParamsCached = null;

const queryConfig = {
    options: ({
        bounds,
        expandedSearchHasModifiedMapViewport,
        params,
        inputParams,
        inputParamsAreChanged,
        inputParamsAreUpdating,
        siteName,
        searchResultsLoading
    }) => {
        let requestParams = {};

        if (inputParamsAreUpdating) {
            // use cached values
            requestParams = requestParamsCached;
        } else if (!inputParamsAreChanged) {
            // use cached search results if inputParams are equal to params
            requestParams = requestParamsCached = searchQueryParams(
                siteName,
                params
            );
        } else {
            // make a query if we can't use cached search results
            requestParams = requestParamsCached = {
                site: siteName,
                ...pick({...params, ...inputParams}, ResultsParams),
                pageSize: 0
            };
        }

        /* If expanded search has modified the map viewport (expaned search groups has been uncollapsed)
        then we need update the bounding box to include the expanded searches for the filter count. Note here
        we do not use the bounds in the searchParams but from the transative map state */
        if (expandedSearchHasModifiedMapViewport) {
            requestParams = {...requestParams, ...bounds};
        }

        return {
            errorPolicy: 'all',
            variables: {
                params: requestParams
            },
            ssr: false, // don't need to ssr here, since we already fetched in the application route handler
            skip: searchResultsLoading
        };
    }
};

export default compose(
    connect(
        mapStateToProps,
        {}
    ),
    graphql(filteredCountQuery, queryConfig)
)(FilteredCountDataProvider);
